import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  Calendar,
  LayoutDashboard,
  ExternalLink,
  Users,
  Code2,
  ClipboardList,
  Menu,
  X,
  Settings,
  Ticket,
  IdCard,
  CreditCard,
  ScanLine,
  AtSign,
} from "lucide-react";
import { LOGO_URL } from "@/lib/content";
import { clearPortalToken } from "@/lib/portalApi";

const NAV_ITEMS = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, match: (p) => p === "/portal" },
  { to: "/portal/events", label: "Events", icon: Calendar, match: (p) => p.startsWith("/portal/events") },
  { to: "/portal/contacts", label: "Contacts", icon: Users, match: (p) => p === "/portal/contacts" },
  { to: "/portal/guestlist", label: "Guest Lists", icon: ClipboardList, match: (p) => p.startsWith("/portal/guestlist") },
  { to: "/portal/scan", label: "Door Check-in", icon: ScanLine, match: (p) => p.startsWith("/portal/scan") },
  { to: "/portal/members", label: "Members", icon: IdCard, match: (p) => p.startsWith("/portal/members") },
  { to: "/portal/payments", label: "Payments", icon: CreditCard, match: (p) => p.startsWith("/portal/payments") },
  { to: "/portal/email", label: "Email Domain", icon: AtSign, match: (p) => p.startsWith("/portal/email") },
  { to: "/portal/embed", label: "Embed On Your Website", icon: Code2, match: (p) => p === "/portal/embed" },
  { to: "/portal/venue", label: "Update venue info", icon: Settings, match: (p) => p === "/portal/venue" },
];

/** Shared portal shell — header + burger drawer */
export default function PortalShell({ children, me, testId }) {
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  // Prevent body scroll while drawer is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [open]);

  const logout = () => {
    clearPortalToken();
    nav("/portal/login", { replace: true });
  };

  return (
    <div data-testid={testId} className="min-h-screen bg-black text-[#F5F5F0]">
      <header className="border-b border-white/10 sticky top-0 z-30 bg-black/85 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 md:px-8 flex items-center justify-between h-20 gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="portal-menu-open"
              aria-label="Open menu"
              className="inline-flex items-center justify-center h-11 w-11 border border-white/20 hover:border-[#D9A6F5] hover:text-[#D9A6F5] transition-colors"
            >
              <Menu size={18} />
            </button>
            <Link to="/portal" className="shrink-0 flex items-center gap-3">
              <img src={LOGO_URL} alt="Swingle" className="h-10 md:h-12 w-auto object-contain" />
              {me?.venue?.name && (
                <span className="hidden sm:block text-[10px] uppercase tracking-[0.28em] text-[#A3A3A3] border-l border-white/10 pl-3">
                  {me.venue.name}
                </span>
              )}
            </Link>
          </div>
          <div className="flex items-center gap-3">
            {me?.venue?.slug && (
              <a
                href={`/v/${me.venue.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="portal-view-public"
                className="hidden md:inline-flex items-center gap-2 border border-white/20 text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-4 py-2 text-xs uppercase tracking-[0.2em] font-bold transition-colors"
              >
                <ExternalLink size={12} /> View public
              </a>
            )}
            <button
              type="button"
              data-testid="portal-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 text-[#A3A3A3] hover:text-[#D9A6F5] px-3 py-2 text-xs uppercase tracking-[0.2em] transition-colors"
            >
              <LogOut size={14} /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main>{children}</main>

      {/* Burger drawer */}
      <div
        aria-hidden={!open}
        className={`fixed inset-0 z-[60] ${open ? "" : "pointer-events-none"}`}
        data-testid="portal-menu-drawer"
      >
        {/* Backdrop */}
        <div
          onClick={() => setOpen(false)}
          className={`absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${
            open ? "opacity-100" : "opacity-0"
          }`}
        />
        {/* Panel */}
        <aside
          className={`absolute top-0 left-0 h-full w-[86%] max-w-sm bg-[#0A0A0A] border-r border-white/10 shadow-2xl transform transition-transform duration-300 ${
            open ? "translate-x-0" : "-translate-x-full"
          } flex flex-col`}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
        >
          <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
            <div className="flex items-center gap-3 min-w-0">
              <img src={LOGO_URL} alt="Swingle" className="h-10 w-auto object-contain" />
              {me?.venue?.name && (
                <span className="text-[10px] uppercase tracking-[0.24em] text-[#D9A6F5] truncate">
                  {me.venue.name}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="portal-menu-close"
              aria-label="Close menu"
              className="text-[#A3A3A3] hover:text-[#F5F5F0] p-2"
            >
              <X size={20} />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto py-4">
            <ul className="space-y-1 px-3">
              {NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                const active = item.match(loc.pathname);
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      data-testid={`portal-menu-item-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`flex items-center gap-3 px-4 py-3 text-sm uppercase tracking-[0.2em] font-bold transition-colors ${
                        active
                          ? "bg-[#D9A6F5] text-black"
                          : "text-[#F5F5F0] hover:bg-white/5 hover:text-[#D9A6F5]"
                      }`}
                    >
                      <Icon size={16} /> {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="border-t border-white/10 p-4 space-y-3">
            {me?.venue?.slug && (
              <a
                href={`/v/${me.venue.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full border border-white/20 hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold transition-colors"
              >
                <ExternalLink size={12} /> View your public page
              </a>
            )}
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3]">
              <Ticket size={11} /> Signed in as {me?.email || "—"}
            </div>
            <button
              type="button"
              onClick={logout}
              className="flex items-center justify-center gap-2 w-full text-[#A3A3A3] hover:text-red-400 px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
